import { getUser } from "~/utils/auth-client";
import { logActivity } from "~/services/api";

declare global {
    interface Window {
        gtag: (
            command: "config" | "event" | "set",
            targetId: string,
            params?: Record<string, any>
        ) => void;
        dataLayer: any[];
    }
}

export const GA_TRACKING_ID = import.meta.env.VITE_GA_ID;

const track = (event: string, params?: Record<string, any>) => {
    // GA4
    if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", event, { user_id: getUser()?.id, ...params });
    }
    // Backend log — only when user is authenticated
    if (getUser()) {
        logActivity(event, params).catch(() => {});
    }
};

// ─── Init & Page View ────────────────────────────────────────────────────────

export const initAnalytics = () => {
    if (typeof window === "undefined" || !GA_TRACKING_ID) return;
    const user = getUser();
    if (user) {
        window.gtag("config", GA_TRACKING_ID, { user_id: user.id });
    }
};

export const trackPageView = (path: string) => {
    if (typeof window === "undefined" || !window.gtag || !GA_TRACKING_ID) return;
    window.gtag("config", GA_TRACKING_ID, { page_path: path });
};

export const identifyUser = (userId: string) => {
    if (typeof window === "undefined" || !window.gtag || !GA_TRACKING_ID) return;
    window.gtag("config", GA_TRACKING_ID, { user_id: userId });
};

// ─── Auth ────────────────────────────────────────────────────────────────────

export const trackLogin = (method: "email" | "magic_link") => {
    track("login", { method });
};

export const trackLoginError = (reason: string) => {
    track("login_error", { reason });
};

export const trackLogout = () => {
    track("logout");
};

export const trackSignupRequest = (role: "ALUMNI" | "STUDENT") => {
    track("sign_up", { role });
};

export const trackSignupError = (reason: string) => {
    track("sign_up_error", { reason });
};

// ─── Profile ─────────────────────────────────────────────────────────────────

export const trackProfileUpdate = (field: string) => {
    track("profile_update", { field });
};

export const trackProfilePictureUpload = () => {
    track("profile_picture_upload");
};

// ─── Directory ───────────────────────────────────────────────────────────────

export const trackDirectorySearch = (query: string) => {
    track("search", { search_term: query });
};

export const trackMemberView = (memberName: string) => {
    track("view_member", { member_name: memberName });
};

export const trackLinkedInClick = (memberName: string) => {
    track("click_linkedin", { member_name: memberName });
};

export const trackViewToggle = (mode: string) => {
    track("toggle_view", { view_mode: mode });
};

// ─── Donations ───────────────────────────────────────────────────────────────

export const trackDonationPledge = (amount: number, frequency: "ONE_TIME" | "MONTHLY") => {
    track("donation_pledge", { amount, frequency, currency: "MAD" });
};

export const trackDonationDrawerOpened = () => {
    track("donation_drawer_opened");
};

// ─── Events ──────────────────────────────────────────────────────────────────

export const trackEventView = (eventTitle: string) => {
    track("event_view", { event_title: eventTitle });
};

export const trackEventRegister = (eventTitle: string) => {
    track("event_register", { event_title: eventTitle });
};

export const trackEventUnregister = (eventTitle: string) => {
    track("event_unregister", { event_title: eventTitle });
};

// ─── Onboarding ──────────────────────────────────────────────────────────────

export const trackOnboardingStarted = () => {
    track("onboarding_started");
};

export const trackOnboardingCompleted = () => {
    track("onboarding_completed");
};

// ─── WhatsApp ─────────────────────────────────────────────────────────────────

export const trackWhatsAppGroupJoined = () => {
    track("whatsapp_group_joined");
};

// ─── Password ─────────────────────────────────────────────────────────────────

export const trackPasswordResetRequested = () => {
    track("password_reset_requested");
};

// ─── Admin ───────────────────────────────────────────────────────────────────

export const trackMembershipAccepted = (applicantName: string) => {
    track("membership_accepted", { applicant_name: applicantName });
};

export const trackMembershipRejected = (applicantName: string) => {
    track("membership_rejected", { applicant_name: applicantName });
};

export const trackDonationMarkedPaid = (donorName: string, amount: number) => {
    track("donation_marked_paid", { donor_name: donorName, amount, currency: "MAD" });
};
